import React, { useState, useEffect } from 'react';
import AgentChat from '../components/ai/AgentChat';
import { useAuth } from '../context/AuthContext';
import { clientsAPI, aiAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Sparkle, 
  Robot,
  ClipboardText,
  Spinner,
  ShieldCheck,
  Copy,
  Check
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const AIAssistantPage = () => {
  const [aiTab, setAiTab] = useState('chat');
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [suggestion, setSuggestion] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSuggestion = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setGenerating(true);
    setSuggestion('');

    try {
      const response = await aiAPI.suggestCarePlan(selectedClient, additionalContext);
      setSuggestion(response.data.suggestion);
      toast.success('Care plan suggestion generated');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error('Failed to generate suggestion');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(suggestion);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (user?.role !== 'supervisor') {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <ShieldCheck weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
        <h3 className="font-heading text-lg font-medium">Access Restricted</h3>
        <p className="text-muted-foreground text-sm mt-1">
          Only supervisors can access the AI assistant
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
              <Sparkle weight="duotone" className="w-5 h-5 text-primary-foreground" />
            </div>
            AI Care Assistant
          </h1>
          <p className="text-muted-foreground mt-1">
            Ask questions or make changes by chatting — or generate care plan suggestions
          </p>
        </div>
      </div>

      <div className="flex gap-1 rounded-xl bg-muted p-1 w-fit" data-testid="ai-tabs">
        <button onClick={() => setAiTab('chat')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${aiTab === 'chat' ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-ai-chat">Assistant Chat</button>
        <button onClick={() => setAiTab('careplan')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${aiTab === 'careplan' ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-ai-careplan">Care Plan Suggestions</button>
      </div>

      {aiTab === 'chat' ? <AgentChat /> : (<>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Card */}
        <Card className="rounded-2xl border border-border" data-testid="ai-input-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Robot weight="duotone" className="w-5 h-5 text-primary" />
              Care Plan Generator
            </CardTitle>
            <CardDescription>
              Select a client to generate personalized care plan suggestions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select Client *</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="rounded-xl" data-testid="ai-client-select">
                  <SelectValue placeholder="Choose a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Additional Context (Optional)</Label>
              <Textarea
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder="Any specific concerns, recent changes in condition, or focus areas..."
                rows={4}
                className="rounded-xl"
                data-testid="ai-context-input"
              />
            </div>

            <Button
              onClick={handleGenerateSuggestion}
              disabled={generating || !selectedClient}
              className="w-full rounded-xl gap-2"
              data-testid="generate-suggestion-btn"
            >
              {generating ? (
                <>
                  <Spinner className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkle weight="duotone" className="w-4 h-4" />
                  Generate Care Plan Suggestion
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Powered by OpenAI GPT-5.2 • Non-medical recommendations only
            </p>
          </CardContent>
        </Card>

        {/* Output Card */}
        <Card className="rounded-2xl border border-border" data-testid="ai-output-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <ClipboardText weight="duotone" className="w-5 h-5 text-primary" />
                  AI Suggestion
                </CardTitle>
                <CardDescription>
                  Generated care plan recommendations
                </CardDescription>
              </div>
              {suggestion && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl gap-1"
                  onClick={handleCopy}
                  data-testid="copy-suggestion-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {generating ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <Sparkle weight="duotone" className="w-8 h-8 text-primary animate-pulse" />
                </div>
                <p className="text-muted-foreground">Analyzing client data...</p>
              </div>
            ) : suggestion ? (
              <div className="prose prose-sm max-w-none">
                <div className="p-4 rounded-xl bg-muted/30 whitespace-pre-wrap text-sm text-foreground/90" data-testid="ai-suggestion-content">
                  {suggestion}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <Robot weight="duotone" className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground text-center">
                  Select a client and click generate to receive<br />AI-powered care plan suggestions
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Disclaimer */}
      <Card className="rounded-2xl border border-border bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <ShieldCheck weight="duotone" className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground">Important:</strong> AI suggestions are for non-medical home care services only. 
              All recommendations should be reviewed by a supervisor before implementation. 
              This tool does not provide medical advice or replace professional healthcare guidance.
            </div>
          </div>
        </CardContent>
      </Card>
      </>)}
    </div>
  );
};

export default AIAssistantPage;
